import React, { useState } from "react";
import { Link } from "react-router-dom";
import Reveal from "../anim/Reveal";
import Icon from "../icons/Icon";
import Section from "../layout/Section";
import MicroLabel from "../ui/MicroLabel";
import Statement from "../ui/Statement";
import TicketCard from "../ui/TicketCard";
import { SITE_CONTENT } from "../../constants/siteContent";
import styles from "./CaseStudies.module.css";

/*
 * CaseStudies — THE WORK. The page's one void (near-black) block: a grid of
 * ticket cells, one per shipped product (ref s08-s10, the helmet grid).
 *
 * Each cell IS the link: a notched <TicketCard as="a"> holding a real capture of
 * the live product, its name, and what was delivered. Client cells open the
 * client's site; the Zeta cell opens the Google Play listing. There is no
 * browser-chrome frame any more — the ticket is the frame.
 *
 * *** HONESTY RULES — READ BEFORE EDITING ***
 *   - Every word in a cell comes from src/constants/siteContent.js:
 *     caseStudies.items[] (label / name, outcome, website, screenshot) and
 *     ownProduct (name, tagline, launchLabel, playStoreUrl, promo creatives).
 *   - The Zeta cell renders ONLY while ownProduct.status === "live" AND
 *     playStoreUrl is filled. An empty URL renders no cell, never a dead one.
 *   - No years, client logos, metrics, ratings or download counts. None are
 *     stored, so none are shown. Do not copy numbers off a store listing.
 *   - Screenshots are served locally from public/images (NOT hotlinked) and go
 *     through process.env.PUBLIC_URL. A capture that fails to load leaves a
 *     quiet olive plate with the product's name — never a broken image and
 *     never unrelated stock art standing in for the product.
 *
 * Layout: 3 columns (6 cells = 2 rows) with the middle column dropped, like the
 * reference's staggered columns; 2 columns <= 1024px; 1 column <= 560px. The
 * DOM stays in reading order — the stagger is a relative offset, not a reflow.
 *
 * Analytics: the delegated tracker (utils/analyticsAutoTrack.js) reports every
 * cell as an outbound_click with source "work" — it keys off this section's id,
 * so keep id="work". The Play link also carries the same app_store_click marker
 * it has in OwnProduct and on /zeta.
 */

/* TODO(content): UI labels only. They name a block or describe where a link
   goes; none of them asserts anything about a product. */
const LABELS = {
  eyebrow: "The work",
  allWork: "See all work",
  clientTag: "Live site",
  clientHint: "opens the live site in a new tab",
  productTagFallback: "Google Play",
  productHint: "opens the Google Play listing in a new tab"
};

/* TODO(content): the section headline. Re-lined from caseStudies.subtitle
   ("Live products delivered by Appinfigo…") — no new claim. The <em> is the
   serif accent word; the break keeps the two-line sans / serif lockup. */
const HEADLINE = (
  <>
    SHIPPED AND <br />
    <em>LIVE</em>.
  </>
);

/* TODO(content): where "See all work" points. Must be a real route. */
const ALL_WORK_PATH = "/case-studies";

/* TODO(content): which Zeta launch creative stands in for the app in the grid.
   A filename from ownProduct.promo.gallery; falls back to promo.hero. This one
   carries no price, delivery time or offer, so it cannot go stale. */
const PRODUCT_SHOT_FILE = "zeta-categories.jpg";
const PRODUCT_SHOT_DIR = "/images/zeta/";

const TITLE_ID = "work-title";

const WORK = SITE_CONTENT.caseStudies || {};
const PRODUCT = SITE_CONTENT.ownProduct || {};
const PROMO = PRODUCT.promo || {};

function clean(value) {
  return typeof value === "string" ? value.trim() : "";
}

/* Names are authored as "Brand | Descriptor" — the brand is the part we show. */
function brandOf(name) {
  return clean(String(name || "").split("|")[0]);
}

/* Local public/ assets must go through PUBLIC_URL (CRA); an absolute URL is
   passed through untouched. */
function resolveAsset(src) {
  const path = clean(src);
  if (!path) return "";
  return /^https?:\/\//i.test(path) ? path : `${process.env.PUBLIC_URL}${path}`;
}

/* ---- client work: one cell per caseStudies item that has a live URL ---- */
const CLIENT_CELLS = (Array.isArray(WORK.items) ? WORK.items : [])
  .filter((item) => item && clean(item.website) && clean(item.name))
  .map((item, index) => {
    const brand = brandOf(item.name);
    const label = clean(item.label) || brand;

    return {
      id: `work-${index}`,
      label,
      tag: LABELS.clientTag,
      outcome: clean(item.outcome),
      href: clean(item.website),
      image: resolveAsset(item.screenshot),
      alt: `${brand}, screenshot of the live product`,
      linkName: `${label}, ${LABELS.clientHint}`
    };
  });

/* ---- our own product: only while it is genuinely live on Google Play ---- */
const PLAY_URL = clean(PRODUCT.playStoreUrl);
const PRODUCT_NAME = clean(PRODUCT.name);
const PRODUCT_IS_LIVE = PRODUCT.status === "live" && Boolean(PLAY_URL);

const PRODUCT_SHOT =
  (Array.isArray(PROMO.gallery) ? PROMO.gallery : []).find(
    (shot) => shot && shot.file === PRODUCT_SHOT_FILE
  ) || (PROMO.hero && PROMO.hero.file ? PROMO.hero : null);

const PRODUCT_CELLS =
  PRODUCT_IS_LIVE && PRODUCT_NAME
    ? [
        {
          id: "work-own",
          label: PRODUCT_NAME,
          tag: clean(PRODUCT.launchLabel) || LABELS.productTagFallback,
          outcome: clean(PRODUCT.tagline),
          href: PLAY_URL,
          image: PRODUCT_SHOT
            ? resolveAsset(`${PRODUCT_SHOT_DIR}${PRODUCT_SHOT.file}`)
            : "",
          alt: PRODUCT_SHOT
            ? clean(PRODUCT_SHOT.alt) || `${PRODUCT_NAME} launch creative`
            : "",
          linkName: `${PRODUCT_NAME}, ${LABELS.productHint}`,
          /* same marker the Play badge carries in OwnProduct and on /zeta */
          interaction: "app_store_click:google_play"
        }
      ]
    : [];

const CELLS = [...CLIENT_CELLS, ...PRODUCT_CELLS];

/* The closing serif line. Existing copy only: the title of the page the button
   opens (SITE_CONTENT.pages -> /case-studies), else caseStudies.title. */
const WORK_PAGE = (Array.isArray(SITE_CONTENT.pages) ? SITE_CONTENT.pages : []).find(
  (page) => page && page.path === ALL_WORK_PATH
);
const CLOSING_LINE = clean(WORK_PAGE && WORK_PAGE.title) || clean(WORK.title);
const LEAD = clean(WORK.subtitle);

/* Stagger by column, so a row arrives left to right however many rows exist. */
function columnDelay(index) {
  return (index % 3) * 80;
}

/* The capture, on a 4:3 olive plate (the plate is what shows while the image
   loads). Screenshots are cropped from the top, where a site's identity is. */
function Shot({ src, alt, label }) {
  const [failed, setFailed] = useState(!src);

  return (
    <div className={styles.shot} data-surface="olive">
      {failed ? (
        <MicroLabel className={styles.shotFallback} aria-hidden="true">
          {label}
        </MicroLabel>
      ) : (
        <img
          className={styles.image}
          src={src}
          alt={alt}
          loading="lazy"
          decoding="async"
          draggable="false"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}

export default function CaseStudies() {
  if (!CELLS.length) return null;

  return (
    <Section tone="void" size="lg" id="work" aria-labelledby={TITLE_ID}>
      <div className={styles.head}>
        <MicroLabel as="p" className={styles.eyebrow}>
          {LABELS.eyebrow}
        </MicroLabel>
        {/* An h2 inside <main>: HeadingWipe (App.js) owns its motion. */}
        <Statement as="h2" size="h2" id={TITLE_ID} className={styles.title}>
          {HEADLINE}
        </Statement>
        {LEAD ? <p className={styles.lead}>{LEAD}</p> : null}
      </div>

      {/* list-style: none strips list semantics in Safari / VoiceOver; the
          explicit role restores the "list, 6 items" announcement. */}
      {/* eslint-disable-next-line jsx-a11y/no-redundant-roles */}
      <ul className={styles.grid} role="list">
        {CELLS.map((cell, index) => {
          const outcomeId = `${cell.id}-outcome`;

          return (
            <Reveal
              as="li"
              key={cell.id}
              className={styles.item}
              delay={columnDelay(index)}
            >
              {/* The link's NAME is the product + where it goes; what was
                  delivered is its DESCRIPTION, so a screen reader hears
                  "FlyJed, opens the live site in a new tab" and then the
                  outcome, instead of the whole cell run together. */}
              <TicketCard
                as="a"
                tone="void"
                className={styles.cell}
                href={cell.href}
                target="_blank"
                rel="noreferrer"
                aria-label={cell.linkName}
                aria-describedby={cell.outcome ? outcomeId : undefined}
                data-analytics-interaction={cell.interaction}
              >
                <Shot src={cell.image} alt={cell.alt} label={cell.label} />

                <div className={styles.meta}>
                  <MicroLabel as="h3" className={styles.name}>
                    {cell.label}
                  </MicroLabel>
                  <MicroLabel className={`micro--accent ${styles.tag}`}>
                    {cell.tag}
                    <Icon
                      name="arrow-up-right"
                      size={14}
                      className={styles.tagIcon}
                    />
                  </MicroLabel>
                </div>

                {cell.outcome ? (
                  <p id={outcomeId} className={styles.outcome}>
                    {cell.outcome}
                  </p>
                ) : null}
              </TicketCard>
            </Reveal>
          );
        })}
      </ul>

      <Reveal className={styles.more}>
        {CLOSING_LINE ? (
          <p className={`quote ${styles.moreLine}`}>{CLOSING_LINE}</p>
        ) : null}
        <Link className="btn" to={ALL_WORK_PATH}>
          {LABELS.allWork}
          <Icon name="arrow-right" size={18} />
        </Link>
      </Reveal>
    </Section>
  );
}
